import { Component, ViewChild, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { MemberModel, UpdateMemberSettingsRequest } from '../../../../core/models/data/member.model';
import { MemberService } from '../../../../core/services/member.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { MembersMessagingConstants } from '../../../../core/constants/messaging.constants';

/**
 * A member's own directory visibility (DEV-2725). The tab carries ownerOnly, so only the member
 * themselves ever reaches it, and the API refuses the call from anyone else — nobody can opt a member
 * in or out on their behalf. The organization's own default only seeds new members; changing it never
 * overwrites a choice made here.
 */
@Component({
    selector: 'ms-member-settings',
    standalone: true,
    imports: [ReactiveFormsModule, FaDuotoneIconComponent, InfoModalComponent],
    templateUrl: './member-profile-settings.component.html'
})
export class MemberProfileSettingsComponent {

    private formBuilder = inject(FormBuilder);
    private bootstrapService = inject(BootstrapService);
    private memberService = inject(MemberService);

    public member = input<MemberModel | undefined>(undefined);
    public memberUpdated = output<MemberModel>();

    public MembersMessagingConstants = MembersMessagingConstants;

    @ViewChild('memberSettingsInfoModalRef') infoModalRef!: InfoModalComponent;

    public settingsForm: FormGroup;
    public isSubmitting = signal<boolean>(false);

    // The last value the API confirmed, which is what Cancel reverts to and what "modified" compares
    // against. Seeded from the loaded member rather than assumed, so the toggle reflects whatever the
    // organization default gave this member when they joined.
    private savedIsVisible = signal<boolean>(true);

    private formValues;

    public hasFormBeenModified = computed<boolean>(() =>
        this.formValues()?.isVisibleInDirectory !== this.savedIsVisible()
    );

    public isVisibleInDirectory = computed<boolean>(() => this.formValues()?.isVisibleInDirectory === true);

    constructor() {
        this.settingsForm = this.formBuilder.group({
            isVisibleInDirectory: [true]
        });

        this.formValues = toSignal(this.settingsForm.valueChanges, { initialValue: this.settingsForm.value });

        effect(() => {
            const member = this.member();
            if (!member) return;
            this.applyLoadedState(member.isVisibleInDirectory);
        });
    }

    private applyLoadedState(isVisibleInDirectory: boolean): void {
        this.savedIsVisible.set(isVisibleInDirectory);
        this.settingsForm.patchValue({ isVisibleInDirectory: isVisibleInDirectory }, { emitEvent: true });
        this.settingsForm.markAsPristine();
    }

    public onResetClicked(): void {
        this.applyLoadedState(this.savedIsVisible());
    }

    public async onSaveClicked(): Promise<void> {
        const memberId = this.member()?.memberId;
        if (!memberId || !this.hasFormBeenModified()) return;

        this.isSubmitting.set(true);

        const request: UpdateMemberSettingsRequest = {
            isVisibleInDirectory: this.settingsForm.value.isVisibleInDirectory === true
        };

        const response = await this.memberService.updateMemberSettings(memberId, request);

        if (response.success === true && response.data) {
            this.applyLoadedState(response.data.isVisibleInDirectory);
            this.memberUpdated.emit(response.data);
            this.infoModalRef.modalType.set(ModalTypes.Success);
            this.infoModalRef.message.set(response.message ?? MembersMessagingConstants.MEMBERS_DIRECTORY_VISIBILITY_SAVE_SUCCESS());
        } else {
            this.infoModalRef.modalType.set(ModalTypes.Failure);
            this.infoModalRef.message.set(response.message ?? MembersMessagingConstants.MEMBERS_DIRECTORY_VISIBILITY_SAVE_FAILURE());
        }

        this.isSubmitting.set(false);
        this.bootstrapService.manageModal('#memberSettingsInfoModal', ModalActions.Show);
    }

    public onInfoModalConfirmed(): void {
        this.bootstrapService.manageModal('#memberSettingsInfoModal', ModalActions.Hide);
    }
}
